import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { CDN_TYPE_GAME, imageUrlCDN } from 'utils';

import {
  constants,
  CryptoFormatter,
  CurrencyFormatter,
  isUserActionNeedPhotoVerification,
  isUserNeedVerification,
  NotificationManager,
  PhotoVerificationAction,
  SiteConfigManager,
  useApplicationState,
  useSettings,
} from '@atlas/core';
import { casinoRoutes } from '@atlas/routing';
import { Button, Icon } from '@atlas/ui';

import useSignInRedirects from 'src/components/Auth/useSignInRedirects';
import { SLIDER_CONFIG } from 'src/components/Pages/Casino/LastWinners/LastWinners';
import WinnerInfo from 'src/components/Pages/Casino/LastWinners/WinnerInfo';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';
import { useAccountBlocked } from 'src/hooks/useAccountBlocked';
const { game: casinoGamePath } = casinoRoutes;
const { CRYPTO_CURRENCY_CODE } = constants;

const useCurrencyFormatter = (currency, languageCode) => {
  return useMemo(() => {
    return CRYPTO_CURRENCY_CODE.includes(currency)
      ? new CryptoFormatter(languageCode, currency)
      : new CurrencyFormatter(languageCode, currency);
  }, [currency, languageCode]);
};

const useLanguageConfig = () => {
  const { lang } = useSettings();

  return useMemo(() => {
    const currentLang = SiteConfigManager.getConfig('languages')?.find(
      language => language.code === lang,
    );
    return currentLang?.hreflang || currentLang?.alpha3Code;
  }, [lang]);
};

const formatUserLogin = login => {
  if (login.length <= 3) return `${login.slice(0, 2)}***`;
  if (login.length >= 8) return `${login.slice(0, 6)}***`;
  return `${login.slice(0, -2)}***`;
};

const LastWinnersItem = ({
  autoplayStart,
  autoplayStop,
  isAuthenticated,
  winner: {
    game: { iconUrl, id, isDemoAvailable, name } = {},
    info: { amount, currency, login } = {},
  },
}) => {
  const { redirectToSignIn } = useSignInRedirects();
  const { account, authenticated } = useApplicationState();
  const languageCode = useLanguageConfig();
  const {
    location: { pathname, search },
    push: historyPush,
  } = useHistory();
  const demoButtonStateForSignedInUsers = SiteConfigManager.getConfig(
    'config.config.casino.mobile_demo_logged',
    false,
  );
  const demoButtonStateForNonSignedInUsers = SiteConfigManager.getConfig(
    'config.config.casino.mobile_demo_non_logged',
    false,
  );
  const accountBlocked = useAccountBlocked();

  const [showButtons, setShowButtons] = useState(false);

  const timeout = useRef(null);
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();

  const loginFormatted = useMemo(() => formatUserLogin(login), [login]);
  const formatter = useCurrencyFormatter(currency, languageCode);
  const gameImageUrl = useMemo(
    () => (iconUrl ? imageUrlCDN(iconUrl, CDN_TYPE_GAME) : null),
    [iconUrl],
  );

  useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, []);

  const toggleButtons = useCallback(() => {
    setShowButtons(prev => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }

      if (autoplayStart && prev) {
        autoplayStart();
      } else if (autoplayStop && !prev) {
        autoplayStop();

        timeout.current = setTimeout(() => {
          autoplayStart();
          setShowButtons(false);
        }, SLIDER_CONFIG.buttonTimeout);
      }

      return !prev;
    });
  }, [autoplayStart, autoplayStop]);

  const onPlayGame = useCallback(
    isDemo => async e => {
      e.stopPropagation();

      if (accountBlocked) {
        NotificationManager.display({
          id: 'accountBlocked',
          type: 'accountBlocked',
        });
        return;
      }

      if (
        isAuthenticated &&
        isUserNeedVerification(account) &&
        isUserActionNeedPhotoVerification(account, PhotoVerificationAction.CASINO_GAME)
      ) {
        setShowButtons(false);
        return NotificationManager.display({
          message: 'verificationPopup',
          type: 'verificationPopup',
        });
      }

      const gameUrl = new URL(`${pathname}/${casinoGamePath}/${id}`, location.origin);

      if (!isAuthenticated && !isDemo) {
        redirectToSignIn({ casinoGameId: id, redirectPath: gameUrl.pathname });
        return;
      }

      const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
      if (!canProceedSpentLimit) return;

      const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
      if (!canProceedSelfRegulation) return;
      gameUrl.searchParams.set('from', pathname + search);

      if (isDemo) {
        gameUrl.searchParams.set('demo', 'true');
      }

      historyPush(gameUrl.pathname + gameUrl.search);
    },
    [
      accountBlocked,
      isAuthenticated,
      account,
      pathname,
      id,
      search,
      historyPush,
      redirectToSignIn,
      showSpentLimitPopupIfNeeded,
      showSelfRegulationPopupIfNeeded,
    ],
  );

  const renderGameImage = () => {
    if (gameImageUrl) {
      return (
        <img
          alt={name}
          className="game-item-image"
          decoding="async"
          height="44"
          loading="lazy"
          src={gameImageUrl}
          width="44"
        />
      );
    }

    return (
      <div className="game-item-default-image">
        <Icon height={44} name="casino-coin" width={44} />
      </div>
    );
  };

  return (
    <div
      className="game-item-wining"
      onClick={toggleButtons}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleButtons();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="game-item-wining-container">
        {renderGameImage()}

        <div className="game-item-info">
          <div
            className={cx('game-buttons-wrapper', {
              'game-buttons-wrapper--show': showButtons,
            })}
          >
            {isDemoAvailable &&
              ((authenticated && demoButtonStateForSignedInUsers) ||
                (!authenticated && demoButtonStateForNonSignedInUsers)) && (
                <Button
                  className="block"
                  label="Demo"
                  onClick={onPlayGame(true)}
                  size="big"
                  type="secondary"
                />
              )}
            <Button
              className="block"
              label="Play"
              onClick={onPlayGame(false)}
              size="big"
              type="primary"
            />
          </div>
          <WinnerInfo
            amount={formatter.format(amount)}
            gameName={name}
            loginFormatted={loginFormatted}
          />
        </div>
      </div>
    </div>
  );
};

LastWinnersItem.propTypes = {
  autoplayStart: PropTypes.func.isRequired,
  autoplayStop: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  winner: PropTypes.shape({
    game: PropTypes.shape({
      iconUrl: PropTypes.string,
      id: PropTypes.number,
      isDemoAvailable: PropTypes.bool,
      name: PropTypes.string,
    }),
    info: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
      login: PropTypes.string,
    }),
  }),
};

export default LastWinnersItem;
